import React from 'react'
import { Badge } from '@/components/ui/badge'
import { ChevronRight } from 'lucide-react'

interface Asset {
  id: string
  name: string
  type: string
  size: number
  progress?: number
  status?: 'uploading' | 'analyzing' | 'complete' | 'error'
  saveStatus?: 'saving' | 'saved' | 'failed' | 'unsaved'
  lastSavedAt?: string
  processingSteps?: Array<{
    label: string
    status: 'pending' | 'active' | 'done' | 'error'
  }>
  analysis?: {
    summary?: {
      passed: number
      warnings: number
      failed: number
    }
  }
}

interface AssetCardProps {
  asset: Asset
  onClick: () => void
}

export default function AssetCard({ asset, onClick }: AssetCardProps) {
  return (
    <div
      onClick={onClick}
      className="border border-gray-200 dark:border-gray-800 rounded-lg p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
    >
      {/* Asset name and progress */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded flex items-center justify-center text-xs font-semibold ${
            asset.type === 'image' ? 'bg-blue-100 text-blue-800' : 
            asset.type === 'video' ? 'bg-purple-100 text-purple-800' : 
            'bg-gray-100 text-gray-800'
          }`}>
            {asset.type === 'image' ? 'IMG' : 
             asset.type === 'video' ? 'VID' : 'DOC'}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <div className="font-medium text-gray-900 dark:text-white truncate">
                {asset.name}
              </div>
              {/* Save Status Badge */}
              {asset.saveStatus === 'saving' && (
                <Badge variant="outline" className="bg-yellow-50 text-yellow-800 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-700">
                  💾 Saving...
                </Badge>
              )}
              {asset.saveStatus === 'saved' && (
                <Badge variant="outline" className="bg-green-50 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700">
                  ✓ Saved
                </Badge>
              )}
              {asset.saveStatus === 'failed' && (
                <Badge variant="outline" className="bg-red-50 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700">
                  ⚠ Save failed
                </Badge>
              )}
              {asset.saveStatus === 'unsaved' && (
                <Badge variant="outline" className="bg-gray-50 text-gray-600 border-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600">
                  ○ Not saved
                </Badge>
              )}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {Math.round(asset.size / 1024)} KB
              {asset.lastSavedAt && asset.saveStatus === 'saved' && (
                <span className="ml-2">• Saved {new Date(asset.lastSavedAt).toLocaleTimeString()}</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {asset.status === 'complete' && asset.analysis?.summary && (
            <div className="flex items-center gap-2">
              {asset.analysis.summary.failed > 0 && (
                <div className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 px-2 py-1 rounded text-xs font-medium">
                  {asset.analysis.summary.failed} Failed
                </div>
              )}
              {asset.analysis.summary.warnings > 0 && (
                <div className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 px-2 py-1 rounded text-xs font-medium">
                  {asset.analysis.summary.warnings} Warnings
                </div>
              )}
              {asset.analysis.summary.passed > 0 && (
                <div className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 px-2 py-1 rounded text-xs font-medium">
                  {asset.analysis.summary.passed} Passed
                </div>
              )}
            </div>
          )}
          {(asset.status === 'uploading' || asset.status === 'analyzing') && (
            <div className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 px-2 py-1 rounded text-xs font-medium animate-pulse">
              Processing...
            </div>
          )}
          {/* Only show progress for actively uploading assets */}
          {(asset.status === 'uploading' || asset.status === 'analyzing') && (
            <div className="text-xs text-gray-500">
              {Math.round(asset.progress || 0)}%
            </div>
          )}
          <ChevronRight className="w-4 h-4 text-gray-400" />
        </div>
      </div>
      
      {/* Progress bar - only show for assets being uploaded */}
      {(asset.status === 'uploading' || asset.status === 'analyzing') && (
        <div className="w-full h-2 bg-gray-100 dark:bg-gray-800 rounded mb-3 overflow-hidden">
          <div 
            className="h-2 bg-gradient-to-r from-blue-500 to-indigo-600 transition-all duration-300" 
            style={{ width: `${asset.progress || 0}%` }}
          />
        </div>
      )}
       
      {/* Processing steps */}
      <div className="flex flex-wrap gap-2 text-xs">
        {asset.processingSteps?.map((step, index) => (
          <span
            key={index}
            className={`px-2 py-1 rounded-full border text-xs ${
              step.status === 'done' 
                ? 'bg-green-50 border-green-200 text-green-700 dark:bg-green-900/20 dark:border-green-800 dark:text-green-400'
              : step.status === 'active' 
                ? 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-400'
              : step.status === 'error'
                ? 'bg-red-50 border-red-200 text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400'
              : 'border-gray-200 text-gray-500 dark:border-gray-700 dark:text-gray-400'
            }`}
          >
            {step.label}
          </span>
        ))}
      </div>
    </div>
  )
}


